import { sectionRenderer } from '@theme/section-renderer';
import { compareReviewDatesNewestFirst, getVisibleReviewPages } from '@theme/product-reviews-pagination';

const REVIEW_ITEM_SELECTOR = '[data-review-item]';

class AllProductReviewsNative extends HTMLElement {
  /** @type {HTMLElement | null} */
  #grid = null;

  /** @type {HTMLElement | null} */
  #viewport = null;

  /** @type {HTMLElement | null} */
  #pagination = null;

  /** @type {HTMLElement | null} */
  #pages = null;

  /** @type {HTMLButtonElement | null} */
  #previous = null;

  /** @type {HTMLButtonElement | null} */
  #next = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLElement[]} */
  #items = [];

  /** @type {number} */
  #currentPage = 1;

  /** @type {number} */
  #sourcePageCount = 1;

  /** @type {string} */
  #pageParam = 'page';

  /** @type {Map<number, HTMLElement[]>} */
  #pageCache = new Map();

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.#grid = this.querySelector('[data-review-grid]');
    this.#viewport = this.querySelector('[data-review-viewport]');
    this.#pagination = this.querySelector('[data-review-pagination]');
    this.#pages = this.querySelector('[data-review-pages]');
    this.#previous = this.querySelector('[data-review-previous]');
    this.#next = this.querySelector('[data-review-next]');

    if (!this.#grid || !this.#viewport || !this.#pagination || !this.#pages || !this.#previous || !this.#next) return;

    const source = this.querySelector('[data-review-source]');
    this.#currentPage = this.#readPositiveInteger(source instanceof HTMLElement ? source.dataset.currentPage : '', 1);
    this.#sourcePageCount = this.#readPositiveInteger(source instanceof HTMLElement ? source.dataset.pages : '', 1);
    this.#pageParam = source instanceof HTMLElement ? source.dataset.pageParam || 'page' : 'page';
    this.#items = this.#collectPageItems(this);
    this.#sortCurrentPage();
    this.#pageCache.set(this.#currentPage, this.#items);

    this.#previous.setAttribute('aria-label', this.#pagination.dataset.previousLabel || 'Previous');
    this.#next.setAttribute('aria-label', this.#pagination.dataset.nextLabel || 'Next');
    this.#previous.addEventListener('click', () => this.#goToPage(this.#currentPage - 1));
    this.#next.addEventListener('click', () => this.#goToPage(this.#currentPage + 1));

    this.#finishLoading();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  get #paginationEnabled() {
    return this.dataset.paginationEnabled === 'true';
  }

  get #sortNewestFirst() {
    return this.dataset.sortNewestFirst === 'true';
  }

  get #desktopColumns() {
    const configured = Number.parseInt(this.dataset.reviewColumns || '1', 10);
    return Number.isFinite(configured) ? Math.max(1, configured) : 1;
  }

  get #mobileColumns() {
    const configured = Number.parseInt(this.dataset.reviewColumnsMobile || '1', 10);
    return Number.isFinite(configured) ? Math.max(1, configured) : 1;
  }

  get #pageCount() {
    return this.#paginationEnabled ? this.#sourcePageCount : 1;
  }

  /**
   * @param {string | undefined} value
   * @param {number} fallback
   */
  #readPositiveInteger(value, fallback) {
    const parsed = Number.parseInt(value || '', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  /** @param {number} page */
  async #loadReviewPage(page) {
    const cachedItems = this.#pageCache.get(page);
    if (cachedItems) return cachedItems;

    const section = this.closest('.shopify-section');
    if (!(section instanceof HTMLElement)) throw new Error('Review section missing');
    if (this.#abortController.signal.aborted) return [];

    const url = new URL(window.location.href);
    url.searchParams.set(this.#pageParam, page.toString());
    const html = await sectionRenderer.getSectionHTML(section.id, true, url);
    if (this.#abortController.signal.aborted) return [];

    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const matchingComponent = Array.from(documentFragment.querySelectorAll('all-product-reviews-native')).find(
      (component) => component.dataset.blockId === this.dataset.blockId
    );
    if (!(matchingComponent instanceof HTMLElement)) throw new Error('All product reviews block missing from response');

    const matchingSource = matchingComponent.querySelector('[data-review-source]');
    const renderedPage = this.#readPositiveInteger(
      matchingSource instanceof HTMLElement ? matchingSource.dataset.currentPage : '',
      page
    );
    if (renderedPage !== page) throw new Error('Requested review page was not rendered');

    const items = this.#collectPageItems(matchingComponent, true);
    this.#sortItemsNewestFirst(items);
    this.#pageCache.set(page, items);
    return items;
  }

  /**
   * @param {ParentNode} source
   * @param {boolean} [clone]
   */
  #collectPageItems(source, clone = false) {
    return Array.from(source.querySelectorAll(REVIEW_ITEM_SELECTOR)).flatMap((item) => {
      if (!(item instanceof HTMLElement)) return [];
      return [clone ? /** @type {HTMLElement} */ (item.cloneNode(true)) : item];
    });
  }

  #finishLoading() {
    this.removeAttribute('aria-busy');
    this.querySelector('[data-review-empty]')?.toggleAttribute('hidden', this.#items.length > 0);
    this.#currentPage = Math.min(this.#currentPage, this.#pageCount);
    this.#render();
  }

  #sortCurrentPage() {
    this.#sortItemsNewestFirst(this.#items);
    if (this.#sortNewestFirst) this.#grid?.append(...this.#items);
  }

  /** @param {HTMLElement[]} items */
  #sortItemsNewestFirst(items) {
    if (!this.#sortNewestFirst) return;
    items.sort((first, second) =>
      compareReviewDatesNewestFirst(first.dataset.reviewSubmittedAt || '', second.dataset.reviewSubmittedAt || '')
    );
  }

  #handleLoadError() {
    this.querySelector('[data-review-load-error]')?.removeAttribute('hidden');
    this.#finishLoading();
  }

  /**
   * @param {number} page
   */
  async #goToPage(page) {
    const targetPage = Math.min(Math.max(page, 1), this.#pageCount);
    if (targetPage === this.#currentPage) return;

    this.setAttribute('aria-busy', 'true');
    try {
      this.#items = await this.#loadReviewPage(targetPage);
    } catch (_error) {
      this.#handleLoadError();
      return;
    }
    if (this.#abortController.signal.aborted || !this.#grid) return;

    this.#currentPage = targetPage;
    this.#grid.replaceChildren(...this.#items);
    this.#finishLoading();
    this.#viewport?.scrollTo({
      top: 0,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    requestAnimationFrame(() => {
      this.#pages?.querySelector(`[data-review-page="${this.#currentPage}"]`)?.focus({ preventScroll: true });
    });
  }

  #render() {
    if (!this.#pagination || !this.#pages || !this.#previous || !this.#next) return;

    for (const [index, item] of this.#items.entries()) {
      item.hidden = false;
      item.dataset.reviewDesktopRow = Math.floor(index / this.#desktopColumns).toString();
      item.dataset.reviewDesktopColumn = (index % this.#desktopColumns).toString();
      item.dataset.reviewMobileRow = Math.floor(index / this.#mobileColumns).toString();
      item.dataset.reviewMobileColumn = (index % this.#mobileColumns).toString();
    }

    this.#pagination.toggleAttribute('hidden', !this.#paginationEnabled || this.#pageCount <= 1);
    this.#previous.disabled = this.#currentPage <= 1;
    this.#next.disabled = this.#currentPage >= this.#pageCount;
    this.#renderPageButtons();
    const pageLabel = (this.#pagination.dataset.pageLabel || 'Page __PAGE__').replace(
      '__PAGE__',
      this.#currentPage.toString()
    );
    const status = this.querySelector('[data-review-status]');
    if (status) status.textContent = pageLabel;
  }

  #renderPageButtons() {
    if (!this.#pages || !this.#pagination) return;
    this.#pages.replaceChildren();

    for (const page of getVisibleReviewPages(this.#pageCount, this.#currentPage)) {
      if (page === null) {
        const gap = document.createElement('span');
        gap.className = 'all-product-reviews-native__pagination-gap';
        gap.textContent = '…';
        gap.setAttribute('aria-hidden', 'true');
        this.#pages.append(gap);
        continue;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'all-product-reviews-native__pagination-button';
      button.dataset.reviewPage = page.toString();
      button.textContent = page.toString();
      button.setAttribute('aria-label', (this.#pagination.dataset.pageLabel || 'Page __PAGE__').replace('__PAGE__', page.toString()));
      if (page === this.#currentPage) button.setAttribute('aria-current', 'page');
      button.addEventListener('click', () => this.#goToPage(page));
      this.#pages.append(button);
    }
  }
}

if (!customElements.get('all-product-reviews-native')) {
  customElements.define('all-product-reviews-native', AllProductReviewsNative);
}
